import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Button, Typography } from '@mui/material';
import DrawerCDS, { DrawerSize } from '@sharedCR/coreDesignSystem/Drawer/DrawerCDS';
import TextFieldCDS from '@sharedCR/coreDesignSystem/form/TextFieldCDS';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { saveConstructionBudgetAction } from 'store/construction/actions';

import { ConstructionTestIds } from '../Construction.testIds';

import { budgetEditRowStyles, budgetEditWrapperStyles } from './BudgetEditDrawer.styles';
import {
  getHasCompleteCreateLine,
  getInitialBudgetDraft,
  getInitialCreateBudgetDraft,
  makeEmptyBudgetLine,
  toBudgetPayload,
  toCreateBudgetPayload,
} from './BudgetEditDrawer.utils';
import BudgetCreateForm from './components/BudgetCreateForm';

const BUDGET_EDIT_FORM_ID = 'construction-budget-edit-form';

const BudgetEditDrawer = ({
  applicationId,
  budget = null,
  budgetLines = [],
  open,
  onClose,
  onSaved,
}) => {
  const dispatch = useDispatch();

  const isCreateMode = _.isNil(budget);

  const [draft, setDraft] = useState({});
  const [createDraft, setCreateDraft] = useState(getInitialCreateBudgetDraft);
  const [isDirty, setIsDirty] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setDraft(getInitialBudgetDraft(budgetLines));
      setCreateDraft(getInitialCreateBudgetDraft());
      setIsDirty(false);
    }
  }, [open, budgetLines]);

  const onAmountChange = useCallback(
    lineId => event => {
      const { value } = event.target;

      setDraft(previous => ({ ...previous, [lineId]: value }));
      setIsDirty(true);
    },
    [],
  );

  const updateLine = useCallback(
    (key, field, value) =>
      setCreateDraft(previous => ({
        ...previous,
        lines: previous.lines.map(line => (line.key === key ? { ...line, [field]: value } : line)),
      })),
    [],
  );

  const makeLineTextChange = useCallback(
    (key, field) => event => {
      updateLine(key, field, event.target.value);
      setIsDirty(true);
    },
    [updateLine],
  );

  const makeLineCategoryChange = useCallback(
    key => (_event, option) => {
      updateLine(key, 'category', option?.id ?? null);
      setIsDirty(true);
    },
    [updateLine],
  );

  const makeRemoveLine = useCallback(
    key => () => {
      setCreateDraft(previous => ({
        ...previous,
        lines: previous.lines.filter(line => line.key !== key),
      }));
      setIsDirty(true);
    },
    [],
  );

  const onAddLine = useCallback(() => {
    setCreateDraft(previous => ({
      ...previous,
      lines: [...previous.lines, makeEmptyBudgetLine()],
    }));
    setIsDirty(true);
  }, []);

  const onConstructionTypeChange = useCallback((_event, option) => {
    setCreateDraft(previous => ({ ...previous, constructionType: option?.id ?? null }));
    setIsDirty(true);
  }, []);

  const onContingencyChange = useCallback(event => {
    const { value } = event.target;

    setCreateDraft(previous => ({ ...previous, contingencyAmount: value }));
    setIsDirty(true);
  }, []);

  const onSubmit = useCallback(
    event => {
      event.preventDefault();

      setIsLoading(true);

      const payload = isCreateMode
        ? toCreateBudgetPayload({ draft: createDraft })
        : toBudgetPayload({ budget, budgetLines, draft });

      dispatch(saveConstructionBudgetAction({ applicationId, payload, isCreate: isCreateMode }))
        .then(() => {
          onSaved?.();
          onClose();
        })
        .finally(() => setIsLoading(false));
    },
    [
      dispatch,
      applicationId,
      isCreateMode,
      budget,
      budgetLines,
      draft,
      createDraft,
      onSaved,
      onClose,
    ],
  );

  const hasCompleteCreateLine = useMemo(() => getHasCompleteCreateLine(createDraft), [createDraft]);

  const content = useMemo(
    () => (
      <Box
        component="form"
        id={BUDGET_EDIT_FORM_ID}
        sx={budgetEditWrapperStyles}
        onSubmit={onSubmit}
      >
        {isCreateMode ? (
          <BudgetCreateForm
            draft={createDraft}
            isRemoveDisabled={createDraft.lines.length <= 1}
            makeLineCategoryChange={makeLineCategoryChange}
            makeLineTextChange={makeLineTextChange}
            makeRemoveLine={makeRemoveLine}
            onAddLine={onAddLine}
            onConstructionTypeChange={onConstructionTypeChange}
            onContingencyChange={onContingencyChange}
          />
        ) : (
          budgetLines.map(line => (
            <Box key={line.id} sx={budgetEditRowStyles}>
              <Typography variant="medium14">{line.costCode}</Typography>
              <TextFieldCDS
                dataTestId={ConstructionTestIds.BUDGET_EDIT_FIELD}
                label="Budgeted"
                name={`budget-${line.id}`}
                value={draft[line.id] ?? ''}
                onChange={onAmountChange(line.id)}
              />
            </Box>
          ))
        )}
      </Box>
    ),
    [
      isCreateMode,
      createDraft,
      budgetLines,
      draft,
      onAmountChange,
      onSubmit,
      makeLineCategoryChange,
      makeLineTextChange,
      makeRemoveLine,
      onAddLine,
      onConstructionTypeChange,
      onContingencyChange,
    ],
  );

  const isSaveDisabled = !isDirty || isLoading || (isCreateMode && !hasCompleteCreateLine);

  const actions = (
    <Button
      data-testid={ConstructionTestIds.BUDGET_EDIT_SAVE_BUTTON}
      disabled={isSaveDisabled}
      form={BUDGET_EDIT_FORM_ID}
      type="submit"
      variant="contained"
    >
      {isCreateMode ? 'Create Budget' : 'Save Budget'}
    </Button>
  );

  return (
    <DrawerCDS
      actions={actions}
      content={content}
      dataTestId={ConstructionTestIds.BUDGET_EDIT_DRAWER}
      isDirty={isDirty}
      isLoading={isLoading}
      open={open}
      size={DrawerSize.SMALL}
      title={isCreateMode ? 'Create Construction Budget' : 'Edit Construction Budget'}
      onClose={onClose}
    />
  );
};

BudgetEditDrawer.propTypes = {
  onClose: PropTypes.func.isRequired,
  applicationId: PropTypes.string,
  budget: PropTypes.object,
  budgetLines: PropTypes.array,
  open: PropTypes.bool,
  onSaved: PropTypes.func,
};

export default memo(BudgetEditDrawer);
